import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    safetyPlans: [],
    currentSafetyPlan: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Safety Plans
export const fetchSafetyPlans = createAsyncThunk(
    'safetyPlans/fetchSafetyPlans',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/safety-plans');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Safety Plans'
            );
        }
    }
);

// Fetch a single Safety Plan
export const fetchSafetyPlan = createAsyncThunk(
    'safetyPlans/fetchSafetyPlan',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/safety-plans/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Safety Plan'
            );
        }
    }
);

// Create a new Safety Plan
export const createSafetyPlan = createAsyncThunk(
    'safetyPlans/createSafetyPlan',
    async (planData, { rejectWithValue }) => {
        try {
            const response = await api.post('/safety-plans', planData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create Safety Plan'
            );
        }
    }
);

// Update an existing Safety Plan
export const updateSafetyPlan = createAsyncThunk(
    'safetyPlans/updateSafetyPlan',
    async ({ case_id, planData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/safety-plans/case/${case_id}`, planData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update Safety Plan'
            );
        }
    }
);

// Delete a Safety Plan
export const deleteSafetyPlan = createAsyncThunk(
    'safetyPlans/deleteSafetyPlan',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/safety-plans/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete Safety Plan'
            );
        }
    }
);

// ==================== Slice ====================
const safetyPlansSlice = createSlice({
    name: 'safetyPlans',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSafetyPlan: (state, action) => {
            state.currentSafetyPlan = action.payload;
        },
        clearCurrentSafetyPlan: (state) => {
            state.currentSafetyPlan = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSafetyPlans.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSafetyPlans.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlans = action.payload;
            })
            .addCase(fetchSafetyPlans.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSafetyPlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSafetyPlan.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSafetyPlan = action.payload;
            })
            .addCase(fetchSafetyPlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSafetyPlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSafetyPlan.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlans.unshift(action.payload);
            })
            .addCase(createSafetyPlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSafetyPlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSafetyPlan.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.safetyPlans.findIndex((d) => d.id === updated.id);
                if (index !== -1) {
                    state.safetyPlans[index] = updated;
                }

                if (state.currentSafetyPlan?.id === updated.id) {
                    state.currentSafetyPlan = updated;
                }
            })
            .addCase(updateSafetyPlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSafetyPlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSafetyPlan.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlans = state.safetyPlans.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentSafetyPlan?.id === action.payload) {
                    state.currentSafetyPlan = null;
                }
            })
            .addCase(deleteSafetyPlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentSafetyPlan,
    clearCurrentSafetyPlan,
} = safetyPlansSlice.actions;

export default safetyPlansSlice.reducer;
